<template>
  <!-- ================= LOADING OVERLAY ================= -->
  <div v-if="loading" class="loading-overlay">
    <md-progress-spinner
        md-mode="indeterminate"
        :md-diameter="60"
        :md-stroke="6">
    </md-progress-spinner>

    <div class="loading-text">
      Loading List...
    </div>
  </div>

  <!-- ================= ACTUAL CONTENT ================= -->
  <div v-else>
  <div class="md-layout">

    <!-- Status filter cards -->
    <div
        v-for="(status,index) in statuses"
        :key="'status'+index"
        class="md-layout-item md-medium-size-50 md-small-size-50 md-xsmall-size-100"
        @click="filterStatus(status.name)"
    >
      <md-card>
        <md-card-header class="md-card-header-icon">
          <div class="card-icon" :style="{ backgroundColor: status.color }">
            <md-icon>new_releases</md-icon>
          </div>
          <h4 class="title">{{ status.name }}</h4>
        </md-card-header>
        <md-card-content>
          <div class="md-layout text-center text-primary">
            <h3>{{ statusCount(status.name) }}</h3>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <!-- Clear status filter -->
    <div
        v-if="filters.status"
        class="md-layout-item md-medium-size-50 md-small-size-50 md-xsmall-size-100"
        @click="filterStatus('all')"
    >
      <md-card>
        <md-card-header class="md-card-header-icon icon-rose">
          <div class="card-icon">
            <md-icon>new_releases</md-icon>
          </div>
          <h4 class="title">All</h4>
        </md-card-header>
        <md-card-content>
          <div class="md-layout text-center text-primary">
            <h4>clear status filter</h4>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <!-- Utilization Sheet list -->
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>tag</md-icon>
          </div>
          <h4 class="title">Utilization Sheet List</h4>
        </md-card-header>

        <md-card-content>
          <div class="text-right">
            <md-button @click="goToAdd" class="md-primary md-dense">
              Add Utilization Sheet
            </md-button>
          </div>

          <md-table
              :value="table"
              :md-sort.sync="sortation.field"
              :md-sort-order.sync="sortation.order"
              :md-sort-fn="customSort"
              class="paginated-table table-striped table-hover"
          >
            <md-table-toolbar>
              <!-- Per page -->
              <md-field>
                <label>Per page</label>
                <md-select v-model="pagination.perPage" name="pages">
                  <md-option
                      v-for="item in pagination.perPageOptions"
                      :key="item"
                      :label="item"
                      :value="item"
                  >
                    {{ item }}
                  </md-option>
                </md-select>
              </md-field>

              <!-- Search -->
              <md-field>
                <md-input
                    v-model="query"
                    type="search"
                    class="mb-3"
                    clearable
                    style="width: 200px"
                    placeholder="Search ..."
                />
              </md-field>

              <!-- Date filter -->
              <md-field>
                <label>Start Date</label>
                <md-input
                    type="date"
                    v-model="filters.start_date"
                    @change="getList"
                />
              </md-field>

              <md-field>
                <label>End Date</label>
                <md-input
                    type="date"
                    v-model="filters.end_date"
                    @change="getList"
                />
              </md-field>
            </md-table-toolbar>

            <!-- Table rows -->
            <md-table-row slot="md-table-row" slot-scope="{ item }">
              <md-table-cell md-label="Utilization Sheet No" md-sort-by="invoice_number">
                {{ item.invoice_number }}
                <badge :type="guidanceTypeBadge(item.guidance_type)"> {{ item.guidance_type }}
                  ( {{ item.pay_code }})</badge>
              </md-table-cell>
              <md-table-cell md-label="Department" md-sort-by="department">
                {{ item.department?item.department.name:'' }}
              </md-table-cell>
              <md-table-cell md-label="Vendor" md-sort-by="vendor">
                {{ item.vendor?item.vendor.name:'' }}
              </md-table-cell>
              <md-table-cell md-label="Date Range" md-sort-by="start_date">
                {{ item.start_date ? new Date(item.start_date).toLocaleDateString() : "" }} -
                {{ item.end_date ? new Date(item.end_date).toLocaleDateString() : "" }}
              </md-table-cell>
              <md-table-cell md-label="Utilization Sheet Items">
                {{item.invoice_items_count}}<br />
                <badge :type="guidanceBadge(item.status)">
                  {{ item.status }}
                </badge>
              </md-table-cell>

              <!-- Actions -->
              <md-table-cell md-label="Actions">

                <md-button
                    class="md-icon-button md-raised md-round md-info"
                    @click="goToView(item)"
                    style="margin: 0.2rem"
                >
                  <badge :type="guidanceTypeBadge(item.guidance_type)">
                  <md-icon>search</md-icon>
                  </badge>
                </md-button>

                <md-button
                    v-if="item.locked == 0"
                    class="md-icon-button md-raised md-round md-info"
                    :disabled="regeneratingInvoiceId === item.id"
                    @click="regenerate(item.id)"
                    style="margin: 0.2rem"
                >
                  <md-progress-spinner
                      v-if="regeneratingInvoiceId === item.id"
                      md-mode="indeterminate"
                      :md-diameter="20"
                      :md-stroke="2" />

                  <md-icon v-else>update</md-icon>
                </md-button>

                <md-button
                    v-if="item.locked==0"
                    class="md-icon-button md-raised md-round md-info"
                    @click="goToEdit(item.id)"
                    style="margin: 0.2rem"
                >
                  <md-icon>edit</md-icon>
                </md-button>

                <md-button
                    v-if="item.locked==0"
                    class="md-icon-button md-raised md-round md-danger"
                    @click="destroy(item.id)"
                    style="margin: 0.2rem"
                >
                  <md-icon>delete</md-icon>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>

          <!-- Footer table -->
          <div class="footer-table md-table">
            <table>
              <tfoot>
              <tr>
                <th
                    v-for="item in footerTable"
                    :key="item"
                    class="md-table-head"
                >
                  <div class="md-table-head-container md-ripple md-disabled">
                    <div class="md-table-head-label">{{ item }}</div>
                  </div>
                </th>
              </tr>
              </tfoot>
            </table>
          </div>
        </md-card-content>

        <!-- Pagination -->
        <md-card-actions md-alignment="space-between">
          <div>
            <p class="card-category">
              Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
            </p>
          </div>
          <pagination
              class="pagination-no-border pagination-success"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
          />
        </md-card-actions>
      </md-card>
    </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import Pagination from '@/components/Pagination';
import Swal from 'sweetalert2';
import badgeMixin from '@/mixins/badge-mixin';
import Badge from "@/components/Badge.vue";
import formMixin from "@/mixins/form-mixin";

export default {
  components: {Badge, pagination: Pagination },
  mixins: [formMixin, badgeMixin],

  data: () => ({
    filters: {
      status: null,
      start_date: null,
      end_date: null,
    },
    loading:true,
    statuses: [],
    invoices: [],
    table: [],
    regeneratingInvoiceId: null,
    footerTable: [
      'Utilization Sheet No.',
      'Department',
      'Vendor',
      'Date Range',
      'Utilization Sheet Items',
      'Actions',
    ],
    query: null,
    sortation: { field: 'created_at', order: 'asc' },
    pagination: { perPage: 10, currentPage: 1, perPageOptions: [5, 10, 25, 50] },
    total: 0,
  }),

  computed: {
    sort () {
      return this.sortation.order === 'desc'
          ? `-${this.sortation.field}`
          : this.sortation.field;
    },
    from () {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to () {
      let highBound = this.from + this.pagination.perPage;
      return this.total < highBound ? this.total : highBound;
    },
  },

  watch: {
    query: { handler: 'getListDebounced', immediate: true },
    sortation: { handler: 'getList', deep: true },
    pagination: { handler: 'getList', deep: true },
  },

  methods: {
    statusCount (state) {
      return this.invoices.filter(x => x.status === state).length;
    },
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async filterStatus (state) {
      this.filters.status = state !== 'all' ? state : null;
      await this.getList();
    },

    async getList () {
      this.loading=true;
      let filterParams = {};

      if (this.query) {
        filterParams.queryKey = this.query;
      }

      if (this.filters.status) {
        filterParams.status = this.filters.status;
      }

      if (this.filters.start_date) {
        filterParams.start_date = this.filters.start_date;
      }

      if (this.filters.end_date) {
        filterParams.end_date = this.filters.end_date;
      }

      let params = {
        sort: this.sort || undefined,
        filter: filterParams,
       // include:'invoiceItems,department,department.port,department.port.station,vendor',
        include:'department,vendor',
        page: {
          number: this.pagination.currentPage,
          size: this.pagination.perPage,
        },
      };
      // let params = {
      //   ...(this.sort ? { sort: this.sort } : {}),
      //   filter: {
      //    ...(this.query ? { queryKey: this.query } : {}),
      //     ...(this.filters.status ? { status: this.filters.status } : {}),
      //     ...(this.filters.start_date ? { start_date: this.filters.start_date } : {}),
      //     ...(this.filters.end_date ? { end_date: this.filters.end_date } : {}),
      //   },
      //   page: {
      //     number: this.pagination.currentPage,
      //     size: this.pagination.perPage,
      //   },
      // };

      try {
        await this.$store.dispatch('invoices/list', params);
        this.table = this.$store.getters['invoices/list'];
        this.invoices = this.$store.getters['invoices/list'];
        this.total = this.$store.getters['invoices/listTotal'];

        let paramsStatus = { filter: { model: 'Invoice' ,is_active:1} };
        await this.$store.dispatch('statusOptions/list', paramsStatus);
        this.statuses = this.$store.getters['statusOptions/list'];
      } catch (e) {
        await this.$store.dispatch('alerts/error', 'Oops, something went wrong!');
      }finally {
        setTimeout(() => {
          this.loading = false
        }, 300)
      }
    },

    goToView (item) {

      let id=item.id
      if(item.guidance_type==='Per Man Hour') {
        this.$router.push({name: 'View Utilization Sheet', params: {id}});
      }else{
        this.$router.push({name: 'View Work Utilization Sheet', params: {id}});
      }
    },
    goToAdd () {
      this.$router.push({ name: 'Add Utilization Sheet' });
    },
    goToEdit (id) {
      this.$router.push({ name: 'Edit Utilization Sheet', params: { id } });
    },

    async regenerate(id) {
      this.regeneratingInvoiceId = id;
      try {
        await this.$store.dispatch('invoices/execute', id);

        const invoice = await this.waitForInvoiceGeneration(id);

        this.invoice = invoice;

        const updatedInvoice = this.$store.getters['invoices/invoice'];

// Update the count in the current table row
        const row = this.invoices.find(item => item.id === id);

        if (row) {
          this.$set(row, 'invoice_items_count', updatedInvoice.invoiceItems?updatedInvoice.invoiceItems.length : 0);
        }

        const totalItems = invoice.invoiceItems?invoice.invoiceItems.length : 0;

        if (totalItems > 0) {
          await this.$store.dispatch(
              'alerts/success',
              `Good, ${totalItems} Items Generated successfully!`
          );
        } else {
          await this.$store.dispatch(
              'alerts/error',
              'No Utilization Sheet item found!'
          );
        }

      } catch (e) {
        this.notifyError(e);
      } finally {
        this.regeneratingInvoiceId = null;
      }
    },
    async waitForInvoiceGeneration(id) {
      const maxAttempts = 30;      // 30 seconds
      const delay = 1000;          // 1 second

      for (let i = 0; i < maxAttempts; i++) {

        await new Promise(resolve => setTimeout(resolve, delay));

        await this.$store.dispatch('invoices/get', {
          id,
          include: 'department,vendor,invoiceItems'
        });

        const invoice = this.$store.getters['invoices/invoice'];

        if (invoice.invoiceItems && invoice.invoiceItems.length > 0) {
          return invoice;
        }
      }

      throw new Error('Invoice generation timed out.');
    },

    async destroy (invoice) {
      const confirmation = await Swal.fire({
        title: 'Delete this Utilization sheet?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it',
      });

      if (confirmation.value === true) {
        try {
          await this.$store.dispatch('invoices/destroy', invoice);
          await this.$store.dispatch('alerts/success', 'Utilization Sheet deleted successfully.');
          await this.getList();
        } catch (e) {
          this.notifyError(e);
          // if (e.response?.data?.errors?.[0]) {
          //   await this.$store.dispatch('alerts/error', e.response.data.errors[0].title);
          // } else {
          //   await this.$store.dispatch('alerts/error', 'Delete failed.');
          // }
        }
      }
    },

    customSort () {
      return false;
    },
  },
};
</script>
<style>
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}

.loading-text {
  margin-top: 12px;
  font-size: 14px;
  color: #555;
}

</style>
